import React, { useState } from 'react';
import { Button, message, Tooltip } from 'antd';
import { sendMessage } from 'api/inbox';
import { getUserId } from 'utils/localstore';
import { unitAndSortServerMessages } from 'utils/inbox';
import { TServerMessage } from 'types/inbox';
import { Prospect } from 'types/common';
import s from './s.module.less';

type Props = {
    targetClient?: Prospect;
    patientId?: number;
    selectConversationId?: string;
    conversationMessageMap: React.MutableRefObject<Record<string, TServerMessage[] | undefined>>;
    setMessageList: (messages: TServerMessage[]) => void;
    scrollToLatestMessage: (timeout?: number, smooth?: boolean) => void;
    resetAutoFetchInterval: () => void;
    refetchConversation: (size: number, clientId?: string) => void;
    refetchAllUnreadCount: () => void;
    fetchConversationSize: React.MutableRefObject<number>;
    urlClientId: React.MutableRefObject<string | undefined>;
    run: () => void;
};

const IntakeFormReminderButton = (props: Props) => {
    const {
        targetClient,
        patientId,
        selectConversationId,
        conversationMessageMap,
        setMessageList,
        scrollToLatestMessage,
        resetAutoFetchInterval,
        refetchConversation,
        refetchAllUnreadCount,
        fetchConversationSize,
        urlClientId,
        run,
    } = props;

    const [sendingIntakeReminder, setSendingIntakeReminder] = useState<boolean>(false);
    const providerId = getUserId();

    const handleSendIntakeFormReminder = async () => {
        if (!targetClient?.id) {
            run();
        }

        const reminderMessage = `Hi ${targetClient?.firstName || targetClient?.lastName || 'Patient'},
Just a quick reminder to complete your intake form to help us prepare for your visit. Please fill it out at your earliest convenience using the link below:
<a href="https://www.helloklarity.com/my/intake-form" target="_blank">https://www.helloklarity.com/my/intake-form</a>
If you have any questions, feel free to reach out. We're here to help!`;

        setSendingIntakeReminder(true);
        const res = await sendMessage({
            providerId: providerId!,
            clientId: targetClient?.id! || patientId!,
            message: reminderMessage.replace(/<[^>]+>/g, ''),
        });
        setSendingIntakeReminder(false);

        if (!res.error) {
            // message.success('Intake form reminder sent successfully!');
            resetAutoFetchInterval();
            setTimeout(() => {
                refetchConversation(fetchConversationSize.current, urlClientId.current);
                refetchAllUnreadCount();
            }, 2000);

            //add local message
            const cacheMessageList = conversationMessageMap.current[selectConversationId!] || [];
            const messageData: TServerMessage = res.data?.data;

            const _message = unitAndSortServerMessages([...(cacheMessageList || []), messageData]);
            conversationMessageMap.current[selectConversationId!] = _message || [];
            setMessageList(_message || []);
            scrollToLatestMessage(100, true);
            //end add local message
        }
    };

    // Only show when patient status is card pending
    if (!targetClient || targetClient.creditCardOnFile) {
        return null;
    }

    return (
        <div className={s.intakeFormReminderWrap}>
            <Button
                className={s.intakeFormReminderBtn}
                loading={sendingIntakeReminder}
                onClick={handleSendIntakeFormReminder}
            >
                <span>Send intake form reminder</span>
                <Tooltip
                    overlayClassName={s.tipCard}
                    overlayInnerStyle={{
                        padding: '16px',
                        borderRadius: '24px',
                    }}
                    color="#FFFFFF"
                    title={
                        <div className={s.tooltipContent}>
                            <div className={s.tooltipTitle}>📩 Intake form reminder message preview</div>
                            <div className={s.tooltipMessage}>
                                <p>Hi [Patient&apos;s Name],</p>
                                <p>
                                    Just a quick reminder to complete your intake form to help us prepare for your
                                    visit. Please fill it out at your earliest convenience using the link below:
                                </p>
                                <p className={s.tooltipLink}>[Intake Form URL]</p>
                                <p>If you have any questions, feel free to reach out. We&apos;re here to help!</p>
                            </div>
                        </div>
                    }
                    placement="top"
                >
                    <svg width="16" height="16" viewBox="0 0 14 14" fill="none" className={s.questionIcon}>
                        <path
                            d="M4.48511 5C4.85118 4.22321 5.83895 3.66667 7.00004 3.66667C8.4728 3.66667 9.6667 4.5621 9.6667 5.66667C9.6667 6.59963 8.81496 7.38338 7.66285 7.6044C7.30125 7.67377 7.00004 7.96514 7.00004 8.33333M7 10.3333H7.00667M13 7C13 10.3137 10.3137 13 7 13C3.68629 13 1 10.3137 1 7C1 3.68629 3.68629 1 7 1C10.3137 1 13 3.68629 13 7Z"
                            stroke="#6B7280"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </Tooltip>
            </Button>
        </div>
    );
};

export default IntakeFormReminderButton;
